import React, { useEffect, useState } from 'react';
import raceApi from '../../../clients/RaceClient';
import { useAuth } from '../../../context/AuthContext';
import PageBreadcrumb from '../../shared/PageBreadcrumb';
import BookingCalendar from '../shared/BookingCalendar';
import DayStrip, { type StripDay } from '../shared/DayStrip';
import SlotRows from '../shared/SlotRows';
import { dayState, slotFitsParty, fitPartyToSlot, canSeatUse } from '../../../utils/seatAvailability';
import { normalizeLocationKey } from '../../../clients/locationApiBase';
import identityApi from '../../../clients/IdentityClient';
import driverApi from '../../../clients/DriverClient';
import { resolvePreferredLocation, savePreferredLocationIfEmpty } from '../../../utils/preferredLocation';
import { formatLocalDate } from '../../../utils/time';
import { guestSeatPriceCents } from '../../../utils/guestPricing';
import { sessionTotals } from '../../../utils/sessionTotals';
import { money, shortDate, windowLabel } from '../../../utils/raceNightFormat';
import type { ActivityOption } from '../../../models/entitlement/ActivityOption';
import type { SlotOption } from '../../../models/entitlement/SlotOption';
import type { GuestSeat } from '../../../models/booking/GuestSeat';
import type {
  SeatPaymentKind, SessionBookResult, SessionQuote, SessionQuoteBody, SessionSeatQuote,
} from '../../../models/sessions/Session';
import SeatRows from '../shared/SeatRows';
import PaymentChoice from '../shared/PaymentChoice';
import GuestPaymentForm from '../shared/GuestPaymentForm';
import { useGuestHold } from '../shared/useGuestHold';
import '../BookingCreate.css';
import '../entitlement/EntitlementWizard.css';
import './BookSession.css';

const VENUES: { key: string; label: string }[] = [
  { key: 'pasadena', label: 'Pasadena' },
  { key: 'tustin', label: 'Tustin' },
  { key: 'kansascity', label: 'Kansas City' },
  { key: 'phoenix', label: 'Phoenix' },
];

type RigClass = 'pro' | 'esports';

const activityLabel = (a: ActivityOption): string => a.title;

// How far ahead the strip and calendar reach. No longer the member's book-ahead window: a membership
// seat beyond it is still bookable with credits or card, and the pay step shows the membership option
// greyed with the server's reason. The server remains the authority on every seat.
const BOOK_AHEAD_DAYS = 60;

// How many day chips the strip offers. Seven covers "this weekend" at a glance; anything further out
// goes through "Pick a date".
const STRIP_DAYS = 7;

// The server's hold window for a seat waiting on a card (the race-nights page uses the same). A hold
// restored or left open past it is released rather than offered for payment.
const HOLD_MS = 15 * 60_000;

// The pay step's wording (spec P2b §6).
const COPY = {
  priceChanged: "Prices changed since you started. Here's the new total.",
  insufficientCredits: "You don't have enough credits for the seats you chose.",
  guestIsYou: 'That email is yours. Each guest needs their own.',
  sessionGone: "That session isn't available any more. Pick another time.",
  quoteFailed: "Couldn't load the prices just now. Please try again.",
  bookFailed: "Couldn't book just now. Please try again.",
  holdExpired: 'Your seat hold expired, so nothing was booked. Pick a time again.',
};

/** The days the strip offers, as ISO strings, starting today. */
export function stripDays(now: Date = new Date()): string[] {
  return Array.from({ length: STRIP_DAYS }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() + i);
    return formatLocalDate(d);
  });
}

/** The last day the calendar offers. */
export const maxBookDate = (now: Date = new Date()): string => {
  const d = new Date(now);
  d.setDate(d.getDate() + BOOK_AHEAD_DAYS);
  return formatLocalDate(d);
};

/**
 * The book call's dedupe key, made once per pay step: a retry of the same step makes no second booking.
 * The Math.random fallback is for environments without crypto.randomUUID (jsdom); it is still a v4 GUID,
 * which is what the server parses.
 */
export function newRequestId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = Math.floor(Math.random() * 16);
    return (ch === 'x' ? r : (r % 4) + 8).toString(16);
  });
}

/** The last step: the quote it shows, exactly what was quoted, and the member's choice per seat. */
/** Every seat has exactly one way to pay, and it can be used: there is no choice for the pay step to offer. */
const nothingToChoose = (quote: SessionQuote): boolean =>
  [quote.member, ...quote.guests].every((seat) => seat.options.length === 1 && seat.options[0].available);

interface PayStep {
  quote: SessionQuote;
  /** Sent again, unchanged, on the book call: the price the member saw is the price of THIS session. */
  body: SessionQuoteBody;
  location: string;
  memberPayment: SeatPaymentKind;
  guestPayments: SeatPaymentKind[];
  requestId: string;
  /** "Prices changed…", shown above the choices after a 409 priceChanged. */
  notice: string | null;
  /** A refusal to show beside the Book button; the member can choose again. */
  error: string | null;
}

/** Keep the member's earlier choice when the new quote still offers it; otherwise the server's suggestion. */
const choose = (seat: SessionSeatQuote, previous?: SeatPaymentKind): SeatPaymentKind =>
  (previous && seat.options.some((o) => o.payment === previous && o.available) ? previous : seat.suggested);

const makePayStep = (
  quote: SessionQuote, body: SessionQuoteBody, location: string,
  previous?: PayStep | null, notice: string | null = null,
): PayStep => ({
  quote, body, location, notice, error: null,
  memberPayment: choose(quote.member, previous?.memberPayment),
  guestPayments: quote.guests.map((g, i) => choose(g, previous?.guestPayments[i])),
  // A new quote is a new pay step, so a new key. The server forgets refusals, so reusing it would also
  // work, but a fresh one can never collide with the booking it just refused. The one exception is
  // onContinue, which keeps the last key for the same session (see lastRequest).
  requestId: newRequestId(),
});

type BookRefusal = Extract<SessionBookResult, { ok: false }>;

/** The member-facing words for a book refusal that keeps the pay step open. */
const refusalMessage = (r: BookRefusal): string => {
  if (r.reason === 'insufficientCredits') return COPY.insufficientCredits;
  if (r.reason === 'guestIsYou') return r.error?.trim() ? r.error : COPY.guestIsYou;
  // paymentNotOffered, minimumCharge, paymentNotStarted, concurrent: the server's text is written for members.
  return r.error?.trim() ? r.error : COPY.bookFailed;
};

const guestName = (g: GuestSeat): string => `${g.firstName} ${g.lastName ?? ''}`.trim();

const BookSession: React.FC = () => {
  const { user, refreshUserProfile, refreshBalance } = useAuth();
  const [location, setLocation] = useState(() =>
    resolvePreferredLocation(
      user?.preferredLocation,
      (key) => VENUES.some((v) => v.key === key),
      normalizeLocationKey,
    ),
  );
  const [activities, setActivities] = useState<ActivityOption[]>([]);
  const [activityId, setActivityId] = useState('');
  const [date, setDate] = useState(() => formatLocalDate(new Date()));
  const [slots, setSlots] = useState<SlotOption[]>([]);
  const [slotId, setSlotId] = useState('');
  const [rigClass, setRigClass] = useState<RigClass>('pro');
  const [guests, setGuests] = useState<GuestSeat[]>([]);
  // Availability for each chip in the strip, keyed by ISO day. Undefined = still loading, which
  // leaves the chip enabled: pre-disabling a day we have not checked would hide real availability.
  const [dayStates, setDayStates] = useState<Record<string, ReturnType<typeof dayState>>>({});
  const [isLoadingDays, setIsLoadingDays] = useState(false);
  // The full calendar is opt-in, behind "Pick a date" — the strip answers the common case.
  const [showCalendar, setShowCalendar] = useState(false);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');
  const [confirmation, setConfirmation] = useState('');
  // A message on the wizard itself: a session gone at quote or book time, a failed quote, an expired hold.
  const [failure, setFailure] = useState<string | null>(null);
  // The member tier's guest discount in basis points, for the wizard's guest estimate only. 0 until
  // loaded or when the tier has none. The pay step's prices are the server's quote.
  const [guestDiscountBp, setGuestDiscountBp] = useState<number>(0);
  // Whether `confirmation` is a real confirmation code or just a booking reference.
  const [hasConfirmationCode, setHasConfirmationCode] = useState(false);
  // "How do you want to pay?": set by Continue, cleared by Back, a booking or a session gone.
  const [payStep, setPayStep] = useState<PayStep | null>(null);
  // The last pay step's key, and the session it was for (venue plus everything quoted). A book whose answer
  // was lost may still have booked, so Back then Continue for the SAME session keeps the key: the server
  // answers with that first booking instead of making a second. Any change to the session, a price change,
  // or a booking that went through (200 or a card step) ends it.
  const lastRequest = React.useRef<{ session: string; requestId: string } | null>(null);
  const sessionKey = (at: string, body: SessionQuoteBody): string => JSON.stringify([at, body]);

  // Load activities for the location and auto-select the first one. Clears the selection when the
  // location has no activities.
  useEffect(() => {
    if (!location) return;
    setSlots([]); setSlotId('');
    raceApi.getActivities(location)
      .then((next) => {
        setActivities(next);
        setActivityId(next.length > 0 ? next[0].activityId : '');
      })
      .catch(() => { setActivities([]); setActivityId(''); });
  }, [location]);

  // The tier's guest discount, for the wizard's guest estimate. Failure is deliberately silent: the
  // estimate falls back to list price, and the pay step quotes the real price regardless.
  useEffect(() => {
    const driverId = user?.driverId;
    if (!driverId) return;
    let cancelled = false;
    // Wrapped in Promise.resolve().then so a client that THROWS synchronously — or returns
    // undefined, as a partial test double does — cannot take the whole page down.
    Promise.resolve()
      .then(() => driverApi.getMembershipByDriverId(driverId))
      .then((m) => {
        const bp = m?.guestDiscountBasisPoints;
        if (!cancelled && typeof bp === 'number' && bp > 0) setGuestDiscountBp(bp);
      })
      .catch(() => { /* keep list price */ });
    return () => { cancelled = true; };
  }, [user?.driverId]);

  // The rates quoted for the slot the member actually picked. Undefined until a slot is chosen,
  // which is why the seat rows render no price rather than a misleading $0.00.
  const selectedSlot = slots.find((s) => s.slotId === slotId);
  // A guest pays the slot's rate less the member tier's guest discount, as the server charges it.
  const proGuestCents = guestSeatPriceCents(selectedSlot?.pricePerPersonCents, guestDiscountBp);
  const esportsGuestCents = guestSeatPriceCents(selectedSlot?.esportsPricePerPersonCents, guestDiscountBp);
  const guestSeatCents = (rig: GuestSeat['rigClass']): number | undefined =>
    (rig === 'pro' ? proGuestCents : esportsGuestCents);
  // Sum of the guest seats at their card rate: an ESTIMATE for the wizard only. The pay step shows the
  // server's quote, and the charge is the server's amountMinor on the 202.
  const guestQuoteCents = guests.reduce<number | undefined>((sum, g) => {
    const c = guestSeatCents(g.rigClass);
    return sum === undefined || c === undefined ? undefined : sum + c;
  }, 0);

  // Everyone racing: the member plus their guests. Drives the rig badges and the day states.
  const people = guests.length + 1;

  // Load each chip's state. One /day call per chip: the month endpoint returns a flat date list
  // with no capacity at all, and /day's own `people` filter checks the TOTAL across both classes
  // (people=7 still returns slots showing pro=6), so the per-class decision has to happen here.
  useEffect(() => {
    if (!location || !activityId) { setDayStates({}); return; }
    let cancelled = false;
    const isoDays = stripDays();
    setIsLoadingDays(true);
    Promise.all(isoDays.map((iso) =>
      raceApi.getDayAvailability(location, iso, activityId)
        .then((daySlots) => [iso, dayState(daySlots, people)] as const)
        // A failed probe must not black out a day the member could book: leave it open and let
        // the slot list be the authority.
        .catch(() => [iso, 'open' as const] as const)))
      .then((entries) => {
        if (cancelled) return;
        setDayStates(Object.fromEntries(entries));
      })
      .finally(() => { if (!cancelled) setIsLoadingDays(false); });
    return () => { cancelled = true; };
  }, [location, activityId, people]);

  const loadSlots = React.useCallback(() => {
    if (!location || !activityId || !date) { setSlots([]); return; }
    setIsLoadingSlots(true);
    raceApi.getDayAvailability(location, date, activityId)
      .then(setSlots)
      .catch(() => setSlots([]))
      .finally(() => setIsLoadingSlots(false));
  }, [location, activityId, date]);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  // The whole card-hold lifecycle (restore, confirm, release, 3DS return, charged state) lives in
  // useGuestHold, shared with the race-nights page. Same storage key as the pass booking page it
  // replaces, so a hold taken on the previous build is still restored.
  const hold = useGuestHold({
    storageKey: 'wor.entitlement.pendingGuestPayment',
    fallbackLocation: location,
    maxHoldAgeMs: HOLD_MS,
    // Arrow, not the bare name: finishBooking is declared further down.
    onBooked: async (reference, bookedLocation, hasCode) => {
      void refreshBalance();
      await finishBooking(reference, bookedLocation, hasCode);
    },
    // Back to the SAME wizard: venue, day, time, party and guest names are all still in state. 'idle'
    // re-arms Continue (it stayed 'loading' through the card step), the released seats are back on
    // sale, and any credits the hold spent are back in the header balance.
    onReleased: () => { setStatus('idle'); loadSlots(); void refreshBalance(); },
    onExpired: () => {
      setStatus('idle'); setFailure(COPY.holdExpired); loadSlots(); void refreshBalance();
    },
  });

  const activity = activities.find((a) => a.activityId === activityId);
  // Only slots the whole party can take, counting BOTH classes — a group splits across them, so
  // filtering on the member's own class alone hid times that were genuinely bookable.
  const visibleSlots = slots.filter((s) => slotFitsParty(s, people));
  const days: StripDay[] = stripDays().map((iso) => ({ iso, state: dayStates[iso] }));

  // Picking a time where a seat's simulator is sold out used to send that seat to the server
  // anyway and come back "That slot was just taken" — misleading, for a slot that was never free
  // on that simulator. Move the seats instead: the class is a preference, the availability is not.
  useEffect(() => {
    if (!slotId) return;
    const chosen = slots.find((s) => s.slotId === slotId);
    if (!chosen) return;
    // The WHOLE party at once. Deciding seat by seat could not see that several seats were
    // competing for the same scarce rigs: with 1 Pro free and three seats on Pro, each seat looked
    // fine on its own and the booking asked for three Pro rigs that did not exist.
    const fitted = fitPartyToSlot(chosen, [rigClass, ...guests.map((g) => g.rigClass)]);
    const [memberRig, ...guestRigs] = fitted;
    if (memberRig !== rigClass) setRigClass(memberRig);
    if (guestRigs.some((rig, i) => rig !== guests[i].rigClass)) {
      setGuests((gs) => gs.map((g, i) => (
        guestRigs[i] === g.rigClass ? g : { ...g, rigClass: guestRigs[i] }
      )));
    }
    // rigClass/guests are read, not tracked: re-running on every seat edit would fight the member
    // as they change a seat. The slot choice is what triggers a re-fit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotId, slots]);

  // Growing the party can make the chosen time too small. Clear it only THEN — clearing on every
  // size change would make the member re-pick a slot that was still perfectly valid.
  useEffect(() => {
    if (!slotId) return;
    const chosen = slots.find((s) => s.slotId === slotId);
    if (chosen && !slotFitsParty(chosen, people)) setSlotId('');
  }, [slotId, slots, people]);

  // Every guest needs a full name before the booking can be made. Without this the wizard happily
  // confirmed blank guests, and the server defaults a missing last name to an empty string rather
  // than refusing it — so an unnamed seat would have been written.
  const guestsNamed = guests.every(
    (g) => g.firstName.trim().length > 0 && (g.lastName ?? '').trim().length > 0,
  );
  const canContinue = Boolean(location && activity && date && slotId) && guestsNamed
    && status !== 'loading' && status !== 'success';

  // A new guest starts on the same simulator the member picked — that is far more often what is
  // wanted than the old hardcoded 'esports', which silently seated guests away from the member and
  // priced them differently. Each row can still be switched individually.
  const addGuest = () =>
    setGuests((g) => [...g, { firstName: '', rigClass, experienceLevel: 'beginner' }]);

  /**
   * Shared tail of both success paths: persist the preferred location and show the receipt.
   *
   * `bookedLocation` is passed in rather than read from `location`, because on a 3DS return the
   * component's state holds a stale preference-derived value — saving that would either write a
   * venue the member never booked or, for a first-time member, `patchUser('')`. Empty values are
   * refused outright.
   *
   * `hasConfirmationCode` decides the LABEL. A raw booking GUID must never be presented to a
   * paying customer as a "confirmation code".
   */
  const finishBooking = async (
    reference: string, bookedLocation: string, hasConfirmationCode: boolean,
  ) => {
    setStatus('success');
    setConfirmation(reference);
    setHasConfirmationCode(hasConfirmationCode);
    if (!user || !bookedLocation) return;
    await savePreferredLocationIfEmpty(
      user.preferredLocation, bookedLocation,
      (loc) => identityApi.patchUser(loc),
      refreshUserProfile,
    );
  };

  /** The time is gone: back to the times, say so, and show what is left. */
  const sessionGone = () => {
    setPayStep(null); setFailure(COPY.sessionGone); setSlotId(''); loadSlots();
  };

  // "Continue": quote the session exactly as picked and open the pay step. Books nothing.
  const onContinue = async () => {
    if (!activity || !user) return;
    setStatus('loading'); setFailure(null);
    // Only seats with a name are real. No client-side driver resolution: the server resolves every
    // seat from its contact fields, and resolve-driver is admin-only.
    const named = guests.filter((g) => g.firstName.trim());
    const body: SessionQuoteBody = {
      date, slotId, activityId: activity.activityId, experienceId: activity.experienceId,
      memberRig: rigClass, guests: named,
    };
    const at = location;
    let quote: SessionQuote | null;
    try {
      quote = await raceApi.quoteSession(at, body);
    } catch {
      setStatus('idle'); setFailure(COPY.quoteFailed);
      return;
    }
    setStatus('idle');
    if (!quote) { sessionGone(); return; }
    // One quote per guest, in order, or the choices below could not line up with the seats.
    if (quote.guests.length !== named.length) { setFailure(COPY.quoteFailed); return; }
    const session = sessionKey(at, body);
    const step = makePayStep(quote, body, at);
    const kept = lastRequest.current?.session === session ? lastRequest.current.requestId : null;
    const next = kept ? { ...step, requestId: kept } : step;
    lastRequest.current = { session, requestId: next.requestId };
    // Nothing to choose and nothing to pay (a seat the membership covers, no guests): Continue books it,
    // rather than a pay step with one option on it (Donovan, 29 Sep).
    const due = sessionTotals(quote, next.memberPayment, next.guestPayments);
    if (due && nothingToChoose(quote) && due.cardCents === 0 && due.credits === 0) {
      await bookStep(next, false);
      return;
    }
    setPayStep(next);
  };

  const totals = payStep
    ? sessionTotals(payStep.quote, payStep.memberPayment, payStep.guestPayments)
    : null;
  const choicesAvailable = Boolean(payStep) && [
    [payStep!.quote.member, payStep!.memberPayment] as const,
    ...payStep!.quote.guests.map((g, i) => [g, payStep!.guestPayments[i]] as const),
  ].every(([seat, kind]) => seat.options.some((o) => o.payment === kind && o.available));
  // Checked here as well as on the server, which refuses it (insufficientCredits): each seat's credits
  // option is priced alone, so only the total can show that together they need more than the balance.
  const creditsShort = Boolean(payStep && totals && totals.credits > payStep.quote.balance);
  const canBook = Boolean(totals) && choicesAvailable && !creditsShort && status !== 'loading';

  // "Book" / "Continue to card": book with each seat's choice and the totals shown.
  const onBook = async () => {
    if (!payStep || !totals || !canBook) return;
    await bookStep(payStep, true);
  };

  /**
   * Books one pay step with its choices and the totals they add up to. `shown` is false when Continue books
   * straight away: the pay step then appears only if the book needs the member (a refusal or a new price).
   */
  const bookStep = async (step: PayStep, shown: boolean) => {
    const totals = sessionTotals(step.quote, step.memberPayment, step.guestPayments);
    if (!totals) return;
    setStatus('loading');
    if (shown) setPayStep({ ...step, error: null });
    let result: SessionBookResult;
    try {
      result = await raceApi.bookSession(step.location, {
        ...step.body,
        memberPayment: step.memberPayment,
        guestPayments: step.guestPayments,
        expectedCardCents: totals.cardCents,
        expectedCredits: totals.credits,
        requestId: step.requestId,
      });
    } catch {
      setStatus('idle'); setPayStep({ ...step, error: COPY.bookFailed });
      return;
    }
    if (result.ok && 'awaitingPayment' in result) {
      // 202: the booking is already holding the seats (and has spent any credit seats). Collect the
      // card, THEN confirm server-side. Stay in 'loading' so Continue can't fire a second booking while
      // the card step is up. begin() persists the hold at once, with the venue that holds it.
      // The key is spent: a released hold booked again is a new booking, not this one's card step.
      lastRequest.current = null;
      setPayStep(null);
      hold.begin(result, step.location);
      void refreshBalance();
      return;
    }
    if (result.ok) {
      lastRequest.current = null;
      setPayStep(null);
      void refreshBalance();
      // Only the server-issued confirmationCode earns the "confirmation code" label; a bare id is a reference.
      await finishBooking(
        result.booked.confirmationCode ?? result.booked.bookingId,
        step.location,
        Boolean(result.booked.confirmationCode),
      );
      return;
    }
    setStatus('idle');
    if (result.status === 409 && result.reason === 'priceChanged' && result.quote) {
      // A new quote is a new key (makePayStep), and it is the one Back then Continue keeps from here.
      const repriced = makePayStep(result.quote, step.body, step.location, step, COPY.priceChanged);
      lastRequest.current = { session: sessionKey(step.location, step.body), requestId: repriced.requestId };
      setPayStep(repriced);
      return;
    }
    if (result.status === 404) { sessionGone(); return; }
    setPayStep({ ...step, error: refusalMessage(result) });
  };

  const breadcrumb = <PageBreadcrumb items={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Book a session' }]} />;

  if (status === 'success') {
    return (
      <>
        {breadcrumb}
        <div className="container py-4">
          <div className="alert alert-success" role="alert">
            <h4>Booked!</h4>
            <p className="mb-0">
              {hasConfirmationCode ? 'Confirmation code' : 'Booking reference'}:{' '}
              <strong>{confirmation}</strong>
            </p>
          </div>
        </div>
      </>
    );
  }

  // The card went through but the booking is not confirmed. This is checked BEFORE the payment
  // step and renders no card affordance at all: the only action offered re-runs the idempotent
  // server-side confirm, never the payment. It also covers the 3DS-return case, where the card
  // was taken on Stripe's page and there is no `pending` from a 202 in this session.
  if (hold.chargedButUnconfirmed) {
    return (
      <>
        {breadcrumb}
        <div className="container py-4">
          <div className="booking-create-content">
            <h4 className="text-white">Pay by card</h4>
            <div className="alert alert-warning" role="alert">
              <strong>Your payment went through.</strong> We couldn&apos;t finish confirming the
              booking.{' '}
              {hold.canRecheck
                ? 'Check the status below, and if it still doesn’t confirm please contact us and we’ll sort it out — do not pay again.'
                : 'Please contact us with the reference below and we’ll sort it out — do not pay again.'}
              {hold.chargedBookingId && <> Reference: <strong>{hold.chargedBookingId}</strong></>}
            </div>
            {/* Offered only when it can actually succeed. On the charged screen a button that
                does nothing is the worst possible feedback, and without a resolvable venue the
                re-check could never reach the API that holds the booking. */}
            {hold.canRecheck && (
              <button
                type="button"
                className="btn btn-outline-light"
                onClick={hold.recheck}
                disabled={hold.isRechecking || !hold.canRecheck}
              >
                {hold.isRechecking ? 'Checking…' : 'Check booking status'}
              </button>
            )}
          </div>
        </div>
      </>
    );
  }

  // Card step. The seats are already held by the provisional booking, so the wizard's selections
  // are locked away: the card, or "Change booking", which releases the hold first.
  if (hold.pending) {
    return (
      <>
        {breadcrumb}
        <div className="container py-4">
          <div className="booking-create-content">
            <h4 className="text-white">Pay by card</h4>
            <p className="text-paragraphs">
              We&apos;re holding this time while you pay for the seats you chose to pay by card.
            </p>

            {/* No page-level error here. A decline used to show three times — Stripe's line in the
                card field, an alert up here, and the form's own under Pay — so the form's alert,
                beside the button a phone user just pressed, is now the only one. No onError
                either: a card failure moves no money, and nothing on that path may touch
                chargedButUnconfirmed, which only a successful confirm may clear. */}
            <GuestPaymentForm
              clientSecret={hold.pending.clientSecret}
              bookingId={hold.pending.bookingId}
              intentId={hold.pending.intentId}
              // The venue the 202 came back for — carried on return_url so a 3DS redirect
              // confirms against the API that actually holds this booking.
              location={hold.heldAt}
              // Straight from the 202 — the server priced this off the persisted tickets.
              amountMinor={hold.pending.amountMinor}
              buyerName={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
              buyerEmail={user?.email ?? ''}
              onPaid={hold.onCardPaid}
              onBack={hold.onBack}
              // A 3DS redirect comes back HERE, where useGuestHold finishes the confirm.
              returnPath="/book"
            />
          </div>
        </div>
      </>
    );
  }

  if (payStep) {
    const venueLabel = VENUES.find((v) => v.key === payStep.location)?.label ?? payStep.location;
    const slot = slots.find((s) => s.slotId === payStep.body.slotId);
    const summary = [
      shortDate(payStep.body.date),
      slot ? windowLabel(slot.startTime, slot.endTime) : null,
      venueLabel,
      activity?.title,
    ].filter(Boolean).join(' · ');
    const dueCard = totals?.cardCents ?? 0;
    return (
      <>
        {breadcrumb}
        <div className="container py-4">
          <div className="booking-create-content">
            <h1 className="book-h1">Book a session</h1>
            <div className="pay-step">
              <div className="pay-step-head">
                <h2 className="pay-step-title">How do you want to pay?</h2>
                <p className="pay-step-summary">{summary}</p>
              </div>

              {payStep.notice && (
                <div className="alert alert-info mb-0" role="status">{payStep.notice}</div>
              )}

              <PaymentChoice
                legend="Your seat"
                idPrefix="pay-member"
                options={payStep.quote.member.options}
                value={payStep.memberPayment}
                onChange={(kind) => setPayStep({ ...payStep, memberPayment: kind, error: null })}
                buyCreditsTo="/checkout?returnTo=book"
              />
              {payStep.quote.guests.map((seat, i) => {
                const g = payStep.body.guests[i];
                return (
                  <PaymentChoice
                    // Guests have no id; their position is their identity on this booking.
                    key={i}
                    legend={`Guest ${i + 1}${g ? ` · ${guestName(g)}` : ''}`}
                    idPrefix={`pay-guest-${i}`}
                    options={seat.options}
                    value={payStep.guestPayments[i]}
                    buyCreditsTo="/checkout?returnTo=book"
                    onChange={(kind) => setPayStep({
                      ...payStep,
                      guestPayments: payStep.guestPayments.map((k, j) => (j === i ? kind : k)),
                      error: null,
                    })}
                  />
                );
              })}

              {creditsShort && (
                <p className="pay-step-hint mb-0">{COPY.insufficientCredits}</p>
              )}
              {payStep.error && (
                <div className="alert alert-warning mb-0" role="alert">{payStep.error}</div>
              )}

              <div className="pay-step-due">
                <div className="pay-step-due-text">
                  <span className="pay-step-due-label">Due now</span>
                  {totals && totals.taxCents > 0 && (
                    <span className="pay-step-due-note">Includes {money(totals.taxCents)} tax</span>
                  )}
                  {totals && totals.credits > 0 && (
                    <span className="pay-step-due-note" data-testid="due-credits">
                      and {totals.credits} {totals.credits === 1 ? 'credit' : 'credits'} from your balance
                    </span>
                  )}
                </div>
                <span className="pay-step-due-amount" data-testid="due-now">{money(dueCard)}</span>
              </div>

              <div className="pay-step-actions">
                <button
                  type="button"
                  className="btn btn-outline-light booking-wizard-nav-button"
                  onClick={() => { setPayStep(null); setStatus('idle'); }}
                  disabled={status === 'loading'}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn-success booking-wizard-nav-button"
                  onClick={onBook}
                  disabled={!canBook}
                >
                  {status === 'loading' ? 'Booking…' : dueCard > 0 ? 'Continue to card' : 'Book'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {breadcrumb}
      <div className="container py-4">
        <div className="booking-create-content">
          <h1 className="book-h1">Book a session</h1>
          {/* ONE COLUMN, top to bottom — the approved mockup. Two columns gave the reader no
              order to follow and let the guest list push the calendar off-screen; here each step
              visibly narrows the one below it. */}
          <div className="wizard">

            <div className="wizard-field">
              <span className="wizard-label" id="loc-label">Location</span>
              <div className="wizard-chips" role="group" aria-labelledby="loc-label">
                {VENUES.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    className="wizard-chip"
                    aria-pressed={location === v.key}
                    onClick={() => setLocation(v.key)}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="wizard-field">
              <span className="wizard-label" id="act-label">Activity</span>
              <div className="wizard-chips" role="group" aria-labelledby="act-label">
                {activities.length === 0 ? (
                  <p className="text-paragraphs mb-0">No activities at this venue.</p>
                ) : activities.map((a) => (
                  <button
                    key={a.activityId}
                    type="button"
                    className="wizard-chip"
                    aria-pressed={activityId === a.activityId}
                    onClick={() => { setActivityId(a.activityId); setSlotId(''); }}
                  >
                    {activityLabel(a)}
                  </button>
                ))}
              </div>
            </div>

            <div className="wizard-field">
              <span className="wizard-label" id="party-size-label">How many racing?</span>
              {/* Settled BEFORE the day and time, because it decides which of them can seat the
                  group. Guest names come after a time is chosen. */}
              <div className="party-stepper" role="group" aria-labelledby="party-size-label">
                <button
                  type="button"
                  className="party-step"
                  aria-label="One fewer"
                  disabled={guests.length === 0}
                  onClick={() => setGuests((g) => g.slice(0, -1))}
                >
                  &minus;
                </button>
                <span className="party-count" data-testid="party-count">{people}</span>
                <button
                  type="button"
                  className="party-step party-step-add"
                  aria-label="One more"
                  onClick={addGuest}
                >
                  +
                </button>
                <span className="party-note">
                  {guests.length === 0
                    ? 'Just you'
                    : `You + ${guests.length} guest${guests.length === 1 ? '' : 's'}`}
                </span>
              </div>
            </div>

            <div className="wizard-field">
              <span className="wizard-label" id="day-strip-label">Day</span>
              <DayStrip
                days={days}
                value={date}
                onChange={(iso) => { setDate(iso); setSlotId(''); setShowCalendar(false); }}
                onPickDate={() => setShowCalendar((v) => !v)}
                isLoading={isLoadingDays}
                labelledBy="day-strip-label"
                canPickDate={maxBookDate() > days[days.length - 1].iso}
              />
              {showCalendar && (
                <BookingCalendar
                  selectedDate={date}
                  onSelectDate={(iso) => { setDate(iso); setSlotId(''); setShowCalendar(false); }}
                  isDateDisabled={(iso) => iso > maxBookDate()}
                />
              )}
            </div>

            <div className="wizard-field">
              <span className="wizard-label" id="slot-rows-label">Time</span>
              <SlotRows
                slots={visibleSlots}
                value={slotId}
                onChange={setSlotId}
                people={people}
                paidSeats={guests.length}
                memberRig={rigClass}
                // The same discount the seat rows and summary use, so the row's "from" figure
                // agrees with them to the cent.
                guestDiscountBp={guestDiscountBp}
                isLoading={isLoadingSlots}
                labelledBy="slot-rows-label"
              />
            </div>

            <div className="wizard-field">
              <span className="wizard-label">Who is racing</span>
              <SeatRows
                seats={guests}
                memberName={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
                memberRigClass={rigClass}
                onMemberRigChange={(r) => setRigClass(r)}
                // Only once a slot is chosen: with no slot there is no capacity to judge against,
                // and disabling toggles before then would block a choice for no reason.
                canUse={selectedSlot
                  ? (seatIndex, rig) => canSeatUse(
                    selectedSlot, [rigClass, ...guests.map((g) => g.rigClass)], seatIndex, rig,
                  )
                  : undefined}
                onChange={setGuests}
                // Nothing yet: how the member's seat is paid is chosen on the pay step, once quoted.
                // "Included" here read as a promise for times the membership doesn't cover.
                memberPriceLabel=""
                proSeatCents={proGuestCents}
                esportsSeatCents={esportsGuestCents}
              />
              {guests.length > 0 && !guestsNamed && (
                <p className="text-paragraphs mb-0" data-testid="guest-name-hint">
                  Add every guest&apos;s first and last name to continue.
                </p>
              )}
            </div>

          </div>

          {failure && (
            <div className="alert alert-warning mt-3" role="alert">{failure}</div>
          )}

          <div className="wizard-summary">
            {/* A guide only: how each seat is paid is chosen on the next step, priced by the server.
                With guests, the figure is their card rate for this slot — never a hardcoded $0 beside
                a party that will be charged. */}
            {guests.length > 0 && guestQuoteCents !== undefined ? (
              <>
                <span className="wizard-summary-line">
                  {guests.length} guest{guests.length === 1 ? '' : 's'} by card &middot; you choose how
                  to pay next
                </span>
                <span className="wizard-summary-total">
                  <span className="wizard-summary-amount">
                    ${(guestQuoteCents / 100).toFixed(2)}
                  </span>
                </span>
              </>
            ) : (
              <span className="wizard-summary-line ms-auto">
                Choose how to pay on the next step
              </span>
            )}
            <button
              type="button"
              className="btn btn-success booking-wizard-nav-button"
              onClick={onContinue}
              disabled={!canContinue}
            >
              {status === 'loading' ? 'Loading…' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookSession;
